import { useMutation, useQuery } from '@tanstack/react-query'
import axios, { AxiosError } from 'axios'

import { PatientShort, SendLoginLinkDto, SerializedPatient, VerificationCodeDto } from 'src/common/types'
import { processApiError } from 'src/common/utils'

const backendUrl = process.env.REACT_APP_MEDPORTAL_API_URL

export const handleSendLoginLink = (signInArgs: SendLoginLinkDto) => {
  return axios
    .post(`${backendUrl}/api/v1/medportal/auth/sendLogin/link`, signInArgs, { withCredentials: false })
    .then((response) => response.data)
}

export const useRequestLoginLink = () => {
  return useMutation<PatientShort, AxiosError, SendLoginLinkDto, () => void>(
    (signInArgs: SendLoginLinkDto) => handleSendLoginLink(signInArgs),
    {
      onError: (err) => processApiError('Login', err),
    }
  )
}

export const handleVerificationCode = (verificationCodeArgs: VerificationCodeDto) => {
  return axios
    .post(`${backendUrl}/api/v1/medportal/auth/verification`, verificationCodeArgs, { withCredentials: true })
    .then((response) => response.data)
}
export const useVerification = (opts: VerificationCodeDto, enabled = true) => {
  return useQuery<PatientShort, AxiosError>(['currentPatientCode', opts], () => handleVerificationCode(opts), {
    onError: (err) => processApiError('Unable to verify patient', err),
    enabled,
    staleTime: 10000,
  })
}

export const getCurrentPatient = async () => {
  const currentPatient = await axios
    .get(`${backendUrl}/api/v1/medportal/auth/whoami`, { withCredentials: true })
    .then((response) => response.data)
  return currentPatient
}
export const useCurrentPatient = () => {
  return useQuery<SerializedPatient, AxiosError>(['currentPatient'], getCurrentPatient, {
    onError: (err) => processApiError('Unable to fetch serialized patient', err),
    staleTime: 10000,
  })
}

export const handleLogoutPatient = () => {
  return axios
    .post(`${backendUrl}/api/v1/medportal/auth/signout`, null, { withCredentials: true })
    .then((response) => response.data)
}
export const useSignout = () => {
  return useMutation<{ status: string }, AxiosError>(() => handleLogoutPatient(), {
    onSuccess: () => {
      window.location.reload() // using browser native features instead of react router to get a full page refresh to pick up any new deployments
    },
    onError: (err) => processApiError('Unable to logout', err),
  })
}
